import { computed, ref } from 'vue'

/**
 * Focus mode — trade the admin chrome for working room.
 *
 * ── why it exists ───────────────────────────────────────────────────────────────────────────────────
 * The question bank is a table somebody works for an hour at a stretch, and the shell around it is sized
 * for navigation, not for that: a 290px sidebar, a 64px header, 24px of page padding and a five-control
 * filter row. On a 1080p screen that is roughly a third of the width and a fifth of the height spent on
 * furniture, and the owner asked for it back.
 *
 * This paragraph used to name a fourth thief — «a centred container that stops at `--breakpoint-2xl`».
 * There was never such a container: that class is Tailwind v4 syntax on a v3.4.17 build and emitted no
 * CSS, so the cap this mode was described as lifting had never been applied. It is gone from
 * AdminLayout.vue rather than repaired, because a table being worked wants the window; see the comment
 * there. What focus mode buys is the chrome and the padding, which is what it was always buying.
 *
 * Focus mode collapses the sidebar to its icon rail, hides the header, tightens the page padding, and
 * folds the filters away. Nothing is REMOVED — the rail still navigates, hovering it still expands it,
 * and the filters are one click away with a badge saying how many are active, because a filtered list
 * that looks unfiltered is worse than a narrow one.
 *
 * ── why the state lives here rather than in the page ────────────────────────────────────────────────
 * Two components on opposite sides of the tree have to agree about it: `AppShell` owns the header and the
 * sidebar margin, `AdminLayout` owns the page padding, and the page owns the toggle. A module-level
 * `ref` is the smallest thing that lets all three read the same value, and it survives navigation, which
 * a per-page ref does not — leaving focus mode by opening a question and coming back would be a bug of
 * exactly the kind we just fixed on this page's pagination.
 *
 * ── why nothing here touches the sidebar's own state ────────────────────────────────────────────────
 * An earlier version of this file collapsed the sidebar by writing `isExpanded = false` through the
 * sidebar context, and remembered the previous value to restore on exit. It did not work, and it failed
 * silently: `useSidebarProvider` publishes `isExpanded` as a COMPUTED —
 *
 *     isExpanded: computed(() => (isMobile.value ? false : isExpanded.value))
 *
 * — so the write went to a readonly ref and was dropped. The test covering it passed anyway, because the
 * test handed the composable a plain writable ref; it proved the mechanism worked against a fixture
 * production never supplies. That is the near-miss pattern this repo keeps re-learning, in a file
 * written the same afternoon as a test guarding against it elsewhere.
 *
 * `AppShell` now hides the sidebar with `v-if` instead. The operator's expand/collapse preference is
 * never written, so it needs no remembering and is simply there again when the chrome comes back.
 */

const STORAGE_KEY = 'moroorato_admin_focus_mode'

const read = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    // Private windows and «block site data» make localStorage THROW rather than return null, and a
    // preference is never worth a blank page.
    return false
  }
}

const enabled = ref(read())
const isFullscreen = ref(false)

/**
 * Was focus mode entered TOGETHER with fullscreen, by the header's single control?
 *
 * It has to be a separate fact from either flag, because the two are also reachable independently:
 * QuestionsPage offers them as two buttons on purpose, and somebody who folds the chrome there and
 * then goes fullscreen by their own means must keep focus mode when they press Esc. Only the paired
 * gesture unwinds as a pair.
 *
 * Deliberately NOT persisted. It describes one gesture, not a preference, and a stale `true` read back
 * from localStorage on the next visit would drop the operator out of focus mode the first time they
 * left fullscreen for an unrelated reason.
 */
const pairedWithFullscreen = ref(false)

const persist = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
  } catch {
    // Same reasoning: the mode still works for this session, it simply will not be remembered.
  }
}

export function useFocusMode() {
  const set = (value) => {
    enabled.value = value
    persist(value)
  }

  const toggle = () => set(!enabled.value)

  /**
   * True fullscreen, which is a different thing from focus mode and deliberately a separate control:
   * focus mode hides OUR chrome, fullscreen hides the BROWSER's. Wanting one does not imply the other,
   * and the Fullscreen API needs a user gesture, which only a click can supply.
   */
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else await document.documentElement.requestFullscreen()
    } catch {
      // Denied by policy, unsupported, or no gesture. The button simply does nothing rather than
      // throwing an unhandled rejection across the app.
    }
  }

  /**
   * Both halves of the header's control, so the pairing lives in ONE place rather than in two
   * components that have to agree about it.
   *
   * `enterBoth` is what the header button calls; `exitBoth` is what the escape chip calls. The flag is
   * cleared BEFORE fullscreen is left, so the `fullscreenchange` this triggers finds nothing to unwind
   * and the two paths cannot fight over the same state.
   */
  const enterBoth = () => {
    set(true)
    pairedWithFullscreen.value = true

    if (!document.fullscreenElement) toggleFullscreen()
  }

  const exitBoth = () => {
    pairedWithFullscreen.value = false
    set(false)

    if (document.fullscreenElement) toggleFullscreen()
  }

  /**
   * Esc leaves fullscreen without telling us, so the button's state is read from the document — and,
   * when the two were entered together, leaving one leaves the other.
   *
   * Without this the operator presses Esc, the browser chrome comes back, and they are left in a page
   * with no header and no sidebar wondering what happened. Esc is the gesture every person already
   * knows for «get me out of this», and it has to mean that here too.
   */
  const syncFullscreen = () => {
    isFullscreen.value = Boolean(document.fullscreenElement)

    if (!isFullscreen.value && pairedWithFullscreen.value) {
      pairedWithFullscreen.value = false
      set(false)
    }
  }

  return {
    enabled,
    isFocusMode: computed(() => enabled.value),
    isFullscreen,
    pairedWithFullscreen,
    enterBoth,
    exitBoth,
    set,
    toggle,
    toggleFullscreen,
    syncFullscreen,
  }
}
